import { atom } from "jotai";
import type {
  AccessSpecifier,
  AttachmentFileData,
  ChatResultItem,
  FilterType,
} from "shared/types";
import type { ChatMember } from "shared/types/chatMembers";

// ─── Chat Mode & Access Specifier ───────────────────────────────────

const sendMessageAccessSpecifierAtom = atom<AccessSpecifier | null>(null);

const defaultSendModeAtom = atom<AccessSpecifier>("public");
export const setChatDefaultSendMode = atom(
  null,
  (_get, set, value: AccessSpecifier) => {
    set(defaultSendModeAtom, value);
  },
);

// Service type the chat is mounted for. Tasks have no mode switcher and
// are always private, so the send mode is forced to "private" for them.
const chatServiceTypeAtom = atom<"ticket" | "task">("ticket");
export const setChatServiceType = atom(
  null,
  (_get, set, value: "ticket" | "task") => {
    set(chatServiceTypeAtom, value);
  },
);

export const chatSendMode = atom<AccessSpecifier>((get) =>
  get(chatServiceTypeAtom) === "task"
    ? "private"
    : (get(sendMessageAccessSpecifierAtom) ?? get(defaultSendModeAtom)),
);
export const setChatSendMode = atom(
  null,
  (get, set, value: AccessSpecifier) => {
    // Task chats are always private; ignore any attempt to change the mode.
    if (get(chatServiceTypeAtom) === "task") return;
    set(sendMessageAccessSpecifierAtom, value);
  },
);
export const resetChatSendMode = atom(null, (_get, set) => {
  set(sendMessageAccessSpecifierAtom, null);
});

// ─── Filter State ───────────────────────────────────────────────────

interface ChatFilterState {
  accessSpecifier: AccessSpecifier | null;
  markAsResolved: boolean;
}

const chatFilterAtom = atom<ChatFilterState>({
  accessSpecifier: null,
  markAsResolved: false,
});

export const chatFilter = atom((get) => get(chatFilterAtom));
export const setChatFilter = atom(null, (_get, set, type: FilterType) => {
  if (type === "all") {
    set(chatFilterAtom, { accessSpecifier: null, markAsResolved: false });
  } else if (type === "resolution") {
    set(chatFilterAtom, { accessSpecifier: null, markAsResolved: true });
  } else {
    set(chatFilterAtom, { accessSpecifier: type, markAsResolved: false });
  }
});

// ─── Drawer State ───────────────────────────────────────────────────

const pinnedDrawerOpenAtom = atom(false);
export const isPinnedDrawerOpen = atom((get) => get(pinnedDrawerOpenAtom));
export const setPinnedDrawerOpen = atom(null, (_get, set, value: boolean) => {
  set(pinnedDrawerOpenAtom, value);
});

const searchDrawerOpenAtom = atom(false);
export const isSearchDrawerOpen = atom((get) => get(searchDrawerOpenAtom));
export const setSearchDrawerOpen = atom(null, (_get, set, value: boolean) => {
  set(searchDrawerOpenAtom, value);
});

const kbDrawerOpenAtom = atom(false);
const kbArticleIdAtom = atom<string | null>(null);

export const isKbDrawerOpen = atom((get) => get(kbDrawerOpenAtom));
export const kbArticleId = atom((get) => get(kbArticleIdAtom));
export const openKbDrawer = atom(null, (_get, set, articleId: string) => {
  set(kbArticleIdAtom, articleId);
  set(kbDrawerOpenAtom, true);
});
export const closeKbDrawer = atom(null, (_get, set) => {
  set(kbDrawerOpenAtom, false);
  set(kbArticleIdAtom, null);
});

// ─── Upload State ───────────────────────────────────────────────────

const isUploadingImagesAtom = atom(false);
export const chatIsUploadingImages = atom((get) => get(isUploadingImagesAtom));
export const setChatUploadingImages = atom(
  null,
  (_get, set, value: boolean) => {
    set(isUploadingImagesAtom, value);
  },
);

const imageUploadErrorAtom = atom<string | null>(null);
export const chatImageUploadError = atom((get) => get(imageUploadErrorAtom));
export const setChatImageUploadError = atom(
  null,
  (_get, set, value: string | null) => {
    set(imageUploadErrorAtom, value);
  },
);

// ─── Mode Banner ────────────────────────────────────────────────────

const modeBannerVisibleAtom = atom(false);
export const isModeBannerVisible = atom((get) => get(modeBannerVisibleAtom));
export const setModeBannerVisible = atom(null, (_get, set, value: boolean) => {
  set(modeBannerVisibleAtom, value);
});

// ─── Grouped Messages ───────────────────────────────────────────────

type GroupedMessages = Record<string, ChatResultItem[]> | null;
const groupedMessagesAtom = atom<GroupedMessages>(null);

export const chatGroupedMessages = atom((get) => get(groupedMessagesAtom));
export const setChatGroupedMessages = atom(
  null,
  (_get, set, value: GroupedMessages) => {
    set(groupedMessagesAtom, value);
  },
);

// ─── Chat Members ───────────────────────────────────────────────────

const chatMembersAtom = atom<ChatMember[]>([]);
export const chatMembers = atom((get) => get(chatMembersAtom));
export const setChatMembers = atom(
  null,
  (_get, set, value: ChatMember[] | ((prev: ChatMember[]) => ChatMember[])) => {
    if (typeof value === "function") {
      set(chatMembersAtom, value(_get(chatMembersAtom)));
    } else {
      set(chatMembersAtom, value);
    }
  },
);

export const chatCustomerMembers = atom((get) =>
  get(chatMembersAtom).filter((m) => m.member_type === "customer"),
);

export const chatResourceMembers = atom((get) =>
  get(chatMembersAtom).filter((m) => m.member_type === "resource"),
);

export const chatExternalMembers = atom((get) =>
  get(chatMembersAtom).filter((m) => m.member_type === "external"),
);

// ─── File Attachments ────────────────────────────────────────────────

const fileAttachmentsAtom = atom<AttachmentFileData[]>([]);

export const chatFileAttachments = atom((get) => get(fileAttachmentsAtom));

export const addChatFileAttachments = atom(
  null,
  (get, set, files: AttachmentFileData[]) => {
    const current = get(fileAttachmentsAtom);
    set(fileAttachmentsAtom, [...current, ...files]);
  },
);

export const updateChatFileAttachment = atom(
  null,
  (
    get,
    set,
    payload: { key: string; updates: Partial<AttachmentFileData> },
  ) => {
    const current = get(fileAttachmentsAtom);
    const index = current.findIndex((f) => f.key === payload.key);
    if (index === -1) return;
    const updated = [...current];
    updated[index] = { ...updated[index], ...payload.updates };
    set(fileAttachmentsAtom, updated);
  },
);

export const removeChatFileAttachment = atom(null, (get, set, key: string) => {
  const current = get(fileAttachmentsAtom);
  set(
    fileAttachmentsAtom,
    current.filter((f) => f.key !== key),
  );
});

export const clearChatFileAttachments = atom(null, (_get, set) => {
  set(fileAttachmentsAtom, []);
});

export const chatHasUploadingFiles = atom((get) => {
  const files = get(fileAttachmentsAtom);
  return files.some((f) => f.status === "Uploading");
});

export const chatHasErrorFiles = atom((get) => {
  const files = get(fileAttachmentsAtom);
  return files.some((f) => f.status === "Error");
});

// ─── Reply To Message ────────────────────────────────────────────────

export interface ReplyToMessageData {
  chatId: string;
  message: string;
  senderDisplayName: string;
  accessSpecifier?: AccessSpecifier;
}

const replyToMessageAtom = atom<ReplyToMessageData | null>(null);

export const chatReplyToMessage = atom((get) => get(replyToMessageAtom));
export const setChatReplyToMessage = atom(
  null,
  (_get, set, value: ReplyToMessageData | null) => {
    set(replyToMessageAtom, value);
  },
);
export const clearChatReplyToMessage = atom(null, (_get, set) => {
  set(replyToMessageAtom, null);
});

// ─── Thread Parent Message ──────────────────────────────────────────

const threadParentMessageAtom = atom<ChatResultItem | null>(null);

export const chatThreadParentMessage = atom((get) =>
  get(threadParentMessageAtom),
);
export const setChatThreadParentMessage = atom(
  null,
  (_get, set, value: ChatResultItem | null) => {
    set(threadParentMessageAtom, value);
  },
);
export const clearChatThreadParentMessage = atom(null, (_get, set) => {
  set(threadParentMessageAtom, null);
});

// ─── Thread Send Mode ──────────────────────────────────────────────

const threadSendModeAtom = atom<AccessSpecifier>("public");

export const chatThreadSendMode = atom((get) => get(threadSendModeAtom));
export const setChatThreadSendMode = atom(
  null,
  (_get, set, value: AccessSpecifier) => {
    set(threadSendModeAtom, value);
  },
);

// ─── Editing Message State ───────────────────────────────────────────

const editingMessageIdAtom = atom<string | null>(null);

export const chatEditingMessageId = atom((get) => get(editingMessageIdAtom));
export const setChatEditingMessageId = atom(
  null,
  (_get, set, value: string | null) => {
    set(editingMessageIdAtom, value);
  },
);

// ─── Editor Focus Requests ──────────────────────────────────────────

const chatEditorFocusRequestAtom = atom<number>(0);

export const chatEditorFocusRequest = atom((get) =>
  get(chatEditorFocusRequestAtom),
);
export const requestChatEditorFocus = atom(null, (get, set) => {
  set(chatEditorFocusRequestAtom, get(chatEditorFocusRequestAtom) + 1);
});

const threadEditorFocusRequestAtom = atom<number>(0);

export const threadEditorFocusRequest = atom((get) =>
  get(threadEditorFocusRequestAtom),
);
export const requestThreadEditorFocus = atom(null, (get, set) => {
  set(threadEditorFocusRequestAtom, get(threadEditorFocusRequestAtom) + 1);
});

// ─── Editor Content Injection Requests ──────────────────────────────

const chatEditorInjectRequestAtom = atom<{
  content: string;
  seq: number;
} | null>(null);

export const chatEditorInjectRequest = atom((get) =>
  get(chatEditorInjectRequestAtom),
);
export const requestChatEditorInject = atom(
  null,
  (get, set, content: string) => {
    const prev = get(chatEditorInjectRequestAtom);
    set(chatEditorInjectRequestAtom, {
      content,
      seq: (prev?.seq ?? 0) + 1,
    });
  },
);

// ─── Unread "New messages" Counter ──────────────────────────────────

const chatUnreadCountAtom = atom<number>(0);

export const chatUnreadCount = atom((get) => get(chatUnreadCountAtom));
export const incrementChatUnreadCount = atom(null, (get, set) => {
  set(chatUnreadCountAtom, get(chatUnreadCountAtom) + 1);
});
export const resetChatUnreadCount = atom(null, (_get, set) => {
  set(chatUnreadCountAtom, 0);
});

// ─── Reset ──────────────────────────────────────────────────────────

export const resetChatStore = atom(null, (_get, set) => {
  set(sendMessageAccessSpecifierAtom, null);
  set(chatFilterAtom, { accessSpecifier: null, markAsResolved: false });
  set(pinnedDrawerOpenAtom, false);
  set(searchDrawerOpenAtom, false);
  set(kbDrawerOpenAtom, false);
  set(kbArticleIdAtom, null);
  set(isUploadingImagesAtom, false);
  set(modeBannerVisibleAtom, false);
  set(groupedMessagesAtom, null);
  set(chatMembersAtom, []);
  set(fileAttachmentsAtom, []);
  set(replyToMessageAtom, null);
  set(threadParentMessageAtom, null);
  set(threadSendModeAtom, "public");
  set(editingMessageIdAtom, null);
  set(chatEditorFocusRequestAtom, 0);
  set(threadEditorFocusRequestAtom, 0);
  set(chatEditorInjectRequestAtom, null);
  set(chatUnreadCountAtom, 0);
});
